import React from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { Link as RouterLink } from 'react-router-dom'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import Radio from '@material-ui/core/Radio'
import Checkbox from '@material-ui/core/Checkbox'
import ChevronRightIcon from 'components/material-symbol/icons/ChevronRight'
import OpenInNewIcon from 'components/material-symbol/icons/OpenInNew'
import CoachUserAvatar from 'components/coach-user-avatar'

export const SettingsSection = ({ eyebrow, subtitle, children, classes }) => (
  <Box className={classes.section}>
    <Box className={classes.card}>
      <Box className={classes.cardHeader}>
        <Typography component="h2" className={classes.cardTitle}>
          {eyebrow}
        </Typography>
        {subtitle ? (
          <Typography component="p" className={classes.sectionSubtitle}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      <Box className={classes.cardDivider} />
      <Box className={classes.cardBody}>{children}</Box>
    </Box>
  </Box>
)

SettingsSection.propTypes = {
  eyebrow: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  children: PropTypes.node.isRequired,
  classes: PropTypes.object.isRequired,
}

export const SettingsStatusBadge = ({ children, classes }) => (
  <span className={classes.statusBadge}>{children}</span>
)

SettingsStatusBadge.propTypes = {
  children: PropTypes.node.isRequired,
  classes: PropTypes.object.isRequired,
}

export const SettingsLinkRow = ({
  to,
  href,
  onClick,
  icon,
  iconWrapStyle,
  iconWrapClassName,
  title,
  description,
  trailing,
  badge,
  completed = false,
  'data-testid': dataTestId,
  classes,
}) => {
  const TrailingIcon = href ? OpenInNewIcon : ChevronRightIcon
  const content = (
    <>
      <div
        className={clsx(classes.rowIcon, iconWrapClassName)}
        style={iconWrapStyle}
      >
        {icon}
      </div>
      <Box className={classes.rowBody}>
        <Typography
          component="p"
          className={clsx(
            classes.rowTitle,
            completed && classes.rowTitleCompleted,
          )}
        >
          {title}
        </Typography>
        {description ? (
          typeof description === 'string' ? (
            <Typography component="p" className={classes.rowDescription}>
              {description}
            </Typography>
          ) : (
            description
          )
        ) : null}
      </Box>
      <Box className={classes.rowTrailing}>
        {trailing}
        {badge ? (
          <SettingsStatusBadge classes={classes}>{badge}</SettingsStatusBadge>
        ) : null}
        {!completed ? <TrailingIcon className={classes.chevron} /> : null}
      </Box>
    </>
  )

  if (completed) {
    return (
      <Box
        className={classes.row}
        data-testid={dataTestId}
        aria-disabled="true"
      >
        {content}
      </Box>
    )
  }

  if (href) {
    return (
      <a
        className={classes.row}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClick}
        data-testid={dataTestId}
        aria-disabled="false"
      >
        {content}
      </a>
    )
  }

  if (to) {
    return (
      <RouterLink
        className={classes.row}
        to={to}
        onClick={onClick}
        data-testid={dataTestId}
        aria-disabled="false"
      >
        {content}
      </RouterLink>
    )
  }

  return (
    <Box
      className={`${classes.row} ${classes.rowButton}`}
      component="button"
      type="button"
      onClick={onClick}
      data-testid={dataTestId}
      aria-disabled="false"
    >
      {content}
    </Box>
  )
}

SettingsLinkRow.propTypes = {
  to: PropTypes.string,
  href: PropTypes.string,
  onClick: PropTypes.func,
  icon: PropTypes.node,
  iconWrapStyle: PropTypes.object,
  iconWrapClassName: PropTypes.string,
  title: PropTypes.string.isRequired,
  description: PropTypes.node,
  trailing: PropTypes.node,
  badge: PropTypes.string,
  completed: PropTypes.bool,
  classes: PropTypes.object.isRequired,
}

export const SettingsToggleRow = ({
  icon,
  title,
  description,
  badge,
  action,
  classes,
  iconClassName,
  nested = false,
}) => (
  <Box className={clsx(classes.toggleRow, nested && classes.nestedToggleRow)}>
    <div className={iconClassName ?? classes.rowIcon}>{icon}</div>
    <Box className={classes.rowBody}>
      <Typography component="p" className={classes.rowTitle}>
        {title}
      </Typography>
      {description ? (
        <Typography component="p" className={classes.rowDescription}>
          {description}
        </Typography>
      ) : null}
    </Box>
    {badge ? (
      <Box className={classes.rowTrailing}>
        <SettingsStatusBadge classes={classes}>{badge}</SettingsStatusBadge>
        {action}
      </Box>
    ) : (
      action
    )}
  </Box>
)

SettingsToggleRow.propTypes = {
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  badge: PropTypes.string,
  action: PropTypes.node.isRequired,
  classes: PropTypes.object.isRequired,
  iconClassName: PropTypes.string,
  nested: PropTypes.bool,
}

export const SettingsOptionCard = ({
  icon,
  title,
  description,
  children,
  classes,
}) => (
  <Box className={classes.optionCard}>
    <Box className={classes.optionHeader}>
      <div className={classes.rowIcon}>{icon}</div>
      <Box className={classes.rowBody}>
        <Typography component="p" className={classes.rowTitle}>
          {title}
        </Typography>
        {description ? (
          <Typography component="p" className={classes.rowDescription}>
            {description}
          </Typography>
        ) : null}
      </Box>
    </Box>
    {children ? <Box className={classes.optionControl}>{children}</Box> : null}
  </Box>
)

SettingsOptionCard.propTypes = {
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  children: PropTypes.node,
  classes: PropTypes.object.isRequired,
}

export const SettingsChoiceRow = ({
  icon,
  title,
  description,
  badge,
  checked,
  onSelect,
  disabled = false,
  classes,
}) => (
  <Box
    className={`${classes.choiceRow} ${classes.rowButton}`}
    component="button"
    type="button"
    onClick={disabled ? undefined : onSelect}
    disabled={disabled}
    aria-pressed={checked}
  >
    <div className={classes.rowIcon}>{icon}</div>
    <Box className={classes.rowBody}>
      <Box className={classes.choiceTitleRow}>
        <Typography component="p" className={classes.rowTitle}>
          {title}
        </Typography>
        {badge ? (
          <SettingsStatusBadge classes={classes}>{badge}</SettingsStatusBadge>
        ) : null}
      </Box>
      {description ? (
        <Typography component="p" className={classes.rowDescription}>
          {description}
        </Typography>
      ) : null}
    </Box>
    <Radio
      checked={checked}
      tabIndex={-1}
      color="default"
      disabled={disabled}
      inputProps={{ 'aria-hidden': true }}
      classes={{
        root: classes.choiceRadioRoot,
        checked: classes.choiceRadioChecked,
      }}
    />
  </Box>
)

SettingsChoiceRow.propTypes = {
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  badge: PropTypes.string,
  checked: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  classes: PropTypes.object.isRequired,
}

export const SettingsCheckboxRow = ({
  icon,
  title,
  description,
  checked,
  onChange,
  disabled = false,
  classes,
  nested = false,
  checkboxIcon,
  checkboxCheckedIcon,
}) => (
  <Box
    className={clsx(
      classes.choiceRow,
      classes.rowButton,
      nested && classes.nestedChoiceRow,
    )}
    component="button"
    type="button"
    onClick={disabled ? undefined : () => onChange(!checked)}
    disabled={disabled}
    aria-pressed={checked}
  >
    <div className={classes.rowIcon}>{icon}</div>
    <Box className={classes.rowBody}>
      <Typography component="p" className={classes.rowTitle}>
        {title}
      </Typography>
      {description ? (
        <Typography component="p" className={classes.rowDescription}>
          {description}
        </Typography>
      ) : null}
    </Box>
    <Checkbox
      checked={checked}
      tabIndex={-1}
      color="default"
      disabled={disabled}
      icon={checkboxIcon}
      checkedIcon={checkboxCheckedIcon}
      inputProps={{ 'aria-hidden': true }}
      classes={{
        root: classes.choiceCheckboxRoot,
        checked: classes.choiceCheckboxChecked,
      }}
    />
  </Box>
)

SettingsCheckboxRow.propTypes = {
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  classes: PropTypes.object.isRequired,
  nested: PropTypes.bool,
  checkboxIcon: PropTypes.node,
  checkboxCheckedIcon: PropTypes.node,
}

export const SettingsAccountRow = ({ name, classes }) => (
  <RouterLink className={classes.accountLink} to="/account">
    <Box className={classes.row}>
      <CoachUserAvatar className={classes.accountAvatar} />
      <Box className={classes.rowBody}>
        <Typography component="p" className={classes.rowTitle}>
          {name}
        </Typography>
        <Typography component="p" className={classes.rowDescription}>
          Account details & preferences
        </Typography>
      </Box>
      <ChevronRightIcon className={classes.chevron} />
    </Box>
  </RouterLink>
)

SettingsAccountRow.propTypes = {
  name: PropTypes.string.isRequired,
  classes: PropTypes.object.isRequired,
}
